
"use client";

import { useState, useRef, useId, useEffect, useCallback } from "react";
import type {
    TooltipPosition,
    TooltipCoords,
    UseTooltipOptions,
    UseTooltipReturn,
} from "./types";
import { calculateTooltipPosition, detectMobile } from "./utils";

export function useTooltip({
    position,
    showDelay,
    hideDelay,
    sheet,
    onShow,
}: UseTooltipOptions): UseTooltipReturn {
    const [isVisible, setIsVisible] = useState(false);
    const [isPositioned, setIsPositioned] = useState(false);
    const [coords, setCoords] = useState<TooltipCoords | null>({ top: -9999, left: -9999 });
    const [actualPosition, setActualPosition] = useState<TooltipPosition>(position);
    const [isMobile, setIsMobile] = useState(false);

    const triggerRef = useRef<HTMLElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const isVisibleRef = useRef(false);
    const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const tooltipId = useId();

    // Mobile detection
    useEffect(() => {
        const handleResize = () => setIsMobile(detectMobile());
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Position calculation
    const updatePosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const result = calculateTooltipPosition({
            triggerRect: triggerRef.current.getBoundingClientRect(),
            tooltipRect: tooltipRef.current.getBoundingClientRect(),
            position,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
        });

        const isMobile = matchMedia("(max-width: 768px)").matches;

        setActualPosition(result.actualPosition);
        setCoords(isMobile && sheet ? null : result.coords);
        setIsPositioned(true);
    }, [position, sheet]);

    // Calculate position when visible
    useEffect(() => {
        if (!isVisible) {
            setIsPositioned(false);
            return;
        }

        // Double RAF for proper rendering
        requestAnimationFrame(() => {
            requestAnimationFrame(updatePosition);
        });
    }, [isVisible, updatePosition]);

    // Reposition on scroll/resize
    useEffect(() => {
        if (!isVisible) return;

        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [isVisible, updatePosition]);

    // Outside click for mobile
    useEffect(() => {
        if (!isVisible || !isMobile) return;

        const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            const isOutside =
                !triggerRef.current?.contains(target) &&
                !tooltipRef.current?.contains(target);

            if (isOutside) {
                isVisibleRef.current = false;
                setIsVisible(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
        };
    }, [isVisible, isMobile]);

    // Cleanup timeouts
    useEffect(() => {
        return () => {
            if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        };
    }, []);

    const openTooltip = useCallback(() => {
        if (isVisibleRef.current) return;

        isVisibleRef.current = true;
        onShow?.();
        setIsVisible(true);
    }, [onShow]);

    const closeTooltip = useCallback(() => {
        if (!isVisibleRef.current) return;

        isVisibleRef.current = false;
        setIsVisible(false);
    }, []);

    const show = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
            showTimeoutRef.current = null;
        }

        if (showDelay > 0) {
            showTimeoutRef.current = setTimeout(() => {
                showTimeoutRef.current = null;
                openTooltip();
            }, showDelay);
        } else {
            openTooltip();
        }
    }, [showDelay, openTooltip]);

    const hide = useCallback(() => {
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
            showTimeoutRef.current = null;
        }
        if (hideDelay > 0) {
            hideTimeoutRef.current = setTimeout(() => {
                hideTimeoutRef.current = null;
                closeTooltip();
            }, hideDelay);
        } else {
            closeTooltip();
        }
    }, [hideDelay, closeTooltip]);

    const toggle = useCallback(() => {
        if (isVisible) {
            hide();
        } else {
            show();
        }
    }, [isVisible, show, hide]);

    // Derived state: only show when visible AND positioned
    const isOpen = isVisible && isPositioned;

    return {
        isOpen,
        isMobile,
        position: actualPosition,
        coords,
        triggerRef,
        tooltipRef,
        tooltipId,
        show,
        hide,
        toggle,
    };
}
